import { useCallback, useEffect, useMemo, useState } from 'react';

import { type FirstClassDocument, type PartialDocument, Category, DocumentSpine } from '../../types';
import {
  type DocumentChunk,
  type DocumentChunkContentMap,
  type DocumentChunkManifest,
  type DocumentChunkMap,
  type DocumentChunkTextContent,
  type WebviewDocumentChunk,
  DocumentChunkType,
  UNCHUNKED_DOCUMENT_CHUNK_ID,
  WebviewChunks,
} from '../../types/chunkedDocuments';
import { isArticle, isFirstClassDocument, notEmpty } from '../../typeValidators';
// eslint-disable-next-line import/no-cycle
import exceptionHandler from '../../utils/exceptionHandler.platform';
import getUrlDomain from '../../utils/getUrlDomain';
import { extractTopLevelNodeIndex } from '../../utils/locationSerialization';
import makeLogger from '../../utils/makeLogger';
import { useDeepEqualMemo } from '../../utils/useDeepEqualMemo';
import { fetchRelatedRSS } from '../methods';
import background from '../portalGates/toBackground';
import { fetchDocumentContent } from '../stateUpdaters/transientStateUpdaters/documentContent';
import { useIsChunkingEnabledForDoc } from '../utils/isChunkingEnabled';
import { unpackChunk } from '../utils/unpackChunk';
import useStatePlusLiveValueRef from '../utils/useStatePlusLiveValueRef';
import { usePartialDocument } from './index';

const logger = makeLogger(__filename);

export const documentSpineToArray = (spine: DocumentSpine): string[] => Object.entries(spine)
  .map(([index, id]) => [Number.parseInt(index, 10), id] as [number, string])
  .sort(([idx1, _id1], [idx2, _id2]) => idx1 - idx2)
  .map(([_, id]) => id);

export function makeUnchunkedWebviewChunk(wordCount = 0, unchunkedContent: string | undefined): WebviewDocumentChunk {
  return {
    content: unchunkedContent ?? '',
    index: 0,
    filename: UNCHUNKED_DOCUMENT_CHUNK_ID,
      // only used for chunked docs, so doesn't need to be accurate.
    html_child_node_count: 0,
      // NOTE: this ID is used to calculate html_child_node_count inside the content frame, do not change.
    id: UNCHUNKED_DOCUMENT_CHUNK_ID,
      // We default the word count of this one chunk for unchunked documents to the word count of the document.
      // As this is the only absolute word count used for this document, the relative progress will still be accurate..
    word_count: wordCount,
    text_character_count: null,
  };
}

export function makeWebviewChunksFromDocumentChunks(
  documentChunks: DocumentChunk[] | null,
  chunkContentMap?: DocumentChunkContentMap,
): WebviewDocumentChunk[] | undefined {
  return documentChunks
    ?.map((chunk, index) => {
      const { id: databaseId, internal_id: id } = chunk;
      if (chunk.html_child_node_count === null) {
        exceptionHandler.captureException(
          'generating WebviewDocumentChunk: text chunk has no html_child_node_count',
          {
            extra: {
              id,
              databaseId,
              index,
              chunkType: chunk.type,
              dataLength: chunk.data.length,
            },
          },
        );
        return undefined;
      }
      return {
        id,
        index,
        filename: chunk.filename,
        content: chunkContentMap?.[id]?.text ?? null,
        html_child_node_count: chunk.html_child_node_count,
        word_count: chunk.word_count,
        text_character_count: chunk.text_character_count,
      };
    })
    ?.filter(notEmpty);
}


/**
 * Loads set of chunks from disk and decrypts them if necessary, returning a map from chunk ID to chunk content.
 */
function useUnpackedDocumentChunks(
  chunkMap: DocumentChunkMap | undefined,
  chunkIds: string[],
  isContentUnpackingDisabled: boolean | undefined,
  docId: string | undefined,
): DocumentChunkContentMap {
  const [chunkContentMap, setChunkContentMap, chunkContentMapRef] =
    useStatePlusLiveValueRef<DocumentChunkContentMap>({});

  const chunksWhichShouldBeLoaded = useMemo(() => {
    if (!chunkMap) {
      return [];
    }
    return chunkIds
      .map((chunkId) => {
        const chunk = chunkMap[chunkId];
        if (!chunk) {
          logger.warn('Could not find chunk with ID', { chunkId });
          return undefined;
        }
        return chunk;
      })
      .filter(notEmpty);
  }, [chunkIds, chunkMap]);


  // NOTE: this useEffect can be expensive because it might decrypt many chunks, on the main thread.
  //   therefore, ensure it is not run too often lest we degrade app performance.
  useEffect(() => {
    if (isContentUnpackingDisabled) {
      return;
    }
    let wasUnmounted = false;

    (async () => {
      const chunksToUnpack = chunksWhichShouldBeLoaded.filter(
        (chunk) => !(chunk.internal_id in chunkContentMapRef.current),
      );
      logger.debug('Unpacking chunks', {
        chunksToUnpack: chunksToUnpack.map((chunk) => [chunk.internal_id, chunk.id]),
        unpackedChunks: Object.values(chunkContentMapRef.current).map((chunk) => chunk?.id),
      });
      const unpackedChunks = await Promise.all(
        chunksToUnpack.map(async (chunk) => {
          const chunkContent = await unpackChunk(chunk, docId);
          if (!chunkContent) {
            return undefined;
          }
          logger.debug('unpacked chunk content', {
            id: chunk.internal_id,
            size: chunkContent.text?.length,
          });
          return chunkContent as DocumentChunkTextContent;
        }),
      );
      if (wasUnmounted) {
        return;
      }
      setChunkContentMap((existingEntries) => {
        const chunkIdsWhichShouldBeLoaded = new Set(chunksWhichShouldBeLoaded.map((chunk) => chunk.internal_id));
        const filteredExistingEntries = Object
          .entries(existingEntries)
          .filter(([chunkId, _]) => chunkIdsWhichShouldBeLoaded.has(chunkId));
        const newEntries = unpackedChunks
          .filter(notEmpty)
          .map((chunk) => [chunk.id, chunk]) as [string, DocumentChunkTextContent | undefined][];
        return Object.fromEntries(
          filteredExistingEntries.concat(newEntries),
        );
      });
    })();
    return () => {
      wasUnmounted = true;
    };
    // we use chunkContentMapRef here instead of chunkContentMap itself to prevent an infinite render loop.
  }, [chunkContentMapRef, chunksWhichShouldBeLoaded, setChunkContentMap, isContentUnpackingDisabled, docId]);
  return chunkContentMap;
}

function useStartChunkIndex(
  doc: PartialDocument<FirstClassDocument, 'currentScrollPosition' | 'id'> | null,
  chunkArray: DocumentChunk[] | null,
): number | undefined {
  const [startChunkIndex, setStartChunkIndex] = useState<number | undefined>();

  // When the document is changed, we need to reset the start chunk index.
  useEffect(() => {
    setStartChunkIndex(undefined);
  }, [doc?.id]);

  useEffect(() => {
    if (startChunkIndex !== undefined) {
      return;
    }
    if (!doc) {
      setStartChunkIndex(undefined);
      return;
    }
    const serializedPosition = doc.currentScrollPosition?.serializedPosition;
    if (!serializedPosition) {
      // doc is loaded but no current scroll position, so this is the first open of this doc.
      setStartChunkIndex(0);
      return;
    }
    if (!chunkArray) {
      return;
    }
    let topLevelNodeIndex = extractTopLevelNodeIndex(serializedPosition);
    if (topLevelNodeIndex === undefined) {
      return;
    }
    let chunkIndex = 0;
    for (const chunk of chunkArray) {
      if (chunk.html_child_node_count === null) {
        exceptionHandler.captureException('chunk is missing top level node count', {
          extra: {
            chunk,
            chunkIndex,
            topLevelNodeIndex,
            serializedPosition,
          },
        });
        break;
      }
      if (topLevelNodeIndex < chunk.html_child_node_count) {
        break;
      }
      topLevelNodeIndex -= chunk.html_child_node_count;
      chunkIndex += 1;
    }
    logger.debug('calculated start chunk index', {
      serializedPosition,
      topLevelNodeIndex,
      chunkIndex,
    });
    setStartChunkIndex(chunkIndex);
  }, [chunkArray, doc, startChunkIndex]);

  return startChunkIndex;
}

/**
 * Loads all text chunks from disk/network. Note that the returned chunk map does NOT include binary chunks e.g.
 * images or audio.
 */
export function useTextChunkMap(
  docId: string | undefined,
  manifest: DocumentChunkManifest | undefined,
): DocumentChunkMap | undefined {
  const [chunkMap, setChunkMap] = useState<DocumentChunkMap | undefined>();
  useEffect(() => {
    if (!manifest || !docId) {
      setChunkMap(undefined);
      return;
    }
    let wasEffectCleanupRun = false;
    (async () => {
      const chunkMap = await background.loadAllTextChunksFromManifest(manifest, docId);
      logger.debug('Loaded chunk map', { manifest: Object.keys(manifest) });
      if (wasEffectCleanupRun) {
        return;
      }
      setChunkMap(chunkMap);
    })();
    return () => {
      wasEffectCleanupRun = true;
    };
  }, [docId, manifest]);
  return chunkMap;
}

export type ChunkedDocumentContentOptions = {
  isContentUnpackingDisabled?: boolean;
};


/**
 * Loads and decrypts 'window' of chunks around the last reading position of the given doc.
 * Chunks are guaranteed to be in order and have images inlined as base64 data URLs.
 *
 * For compatibility, if document is not chunked, returns a one-chunk array with full document content.
 *
 * @param docId
 * @param options
 *
 * @return Object array of chunk contents, plus a function to load or unload a chunk with a given ID.
 */
export function useChunksForDocument(
  docId: string | undefined,
  { isContentUnpackingDisabled }: ChunkedDocumentContentOptions = {},
) {
  const [doc, { isFetching }] = usePartialDocument(
    docId,
    [
      'category',
      'content',
      'currentScrollPosition',
      'html',
      'id',
      'parsed_doc_id',
      'source_specific_data',
      'transientData',
      'url',
      'word_count',
    ],
    {
      shouldPollStateIfMissing: true,
    },
  );

  const isChunkingEnabledForDoc = useIsChunkingEnabledForDoc(doc);

  // TODO: this code is duplicated from useDocumentContentFromState().
  //   eventually that hook code should be removed and any uses of it should use useChunkedDocumentContent() instead.
  const unchunkedContent = useMemo(() => {
    if (!doc) {
      return null;
    }
    if (isChunkingEnabledForDoc !== false) {
      return null;
    }
    if (doc.category === Category.Highlight) {
      return doc.html;
    }
    return doc.transientData.content;
  }, [doc, isChunkingEnabledForDoc]);

  const hasDoc = Boolean(doc);
  const parsedDocId = doc && isFirstClassDocument(doc) && doc?.parsed_doc_id;
  const articleDomain = doc && isArticle(doc) && getUrlDomain(doc.url);

  const toc = useDeepEqualMemo(useMemo(() => doc?.source_specific_data?.epub?.toc, [doc?.source_specific_data?.epub?.toc]));

  useEffect(() => {
    if (!docId || !hasDoc || isContentUnpackingDisabled) {
      return;
    }

    /*
      This is async. It isn't guaranteed the content will be there by the time this function returns.
      This is here to ensure the content is requested whenever we're trying to use it. It's called
      elsewhere too (to pre/load the content) but let's be safe. Even if the other calls are
      accidentally broken / removed, this will save us.
    */
    fetchDocumentContent([docId]);
    if (articleDomain) {
      fetchRelatedRSS([articleDomain]);
    }
  }, [docId, hasDoc, parsedDocId, articleDomain, isContentUnpackingDisabled]);

  const spine = useMemo(
    () =>
      isChunkingEnabledForDoc && doc?.transientData.spine
        ? documentSpineToArray(doc.transientData.spine)
        : undefined,
    [doc?.transientData.spine, isChunkingEnabledForDoc],
  );
  const manifest = useMemo(
    () => isChunkingEnabledForDoc ? doc?.transientData.manifest : undefined,
    [doc?.transientData.manifest, isChunkingEnabledForDoc],
  );

  const chunkMap = useTextChunkMap(doc?.id, manifest);
  const chunkArray = useMemo(() => {
    if (!spine || !chunkMap) {
      return null;
    }
    const chunks = spine.map((id) => chunkMap[id]).filter(notEmpty);
    const emptyChunks = spine
      .map((id, index) => [index, id, notEmpty(chunkMap[id])])
      .filter(([_index, _id, present]) => !present);
    if (emptyChunks.length > 0) {
      exceptionHandler.captureException(
        'Some chunks declared in spine are missing',
        { extra: { emptyChunks, spine, docId } },
      );
    }
    logger.debug('Loaded chunks from spine', { spine });
    return chunks;
  }, [chunkMap, docId, spine]);

  const startChunkIndex = useStartChunkIndex(doc, chunkArray);
  const chunkIndexesToLoad = useMemo(() => {
    if (startChunkIndex === undefined) {
      return undefined;
    }
    return Array.from({ length: 3 }, (_, index) => startChunkIndex - 1 + index).filter(
      (index) => index >= 0,
    );
  }, [startChunkIndex]);

  const [loadedDocumentChunkIds, setLoadedDocumentChunkIds] = useState<{
    [id: string]: boolean;
  }>({});

  useEffect(() => {
    logger.debug('clearing loaded chunks');
    setLoadedDocumentChunkIds({});
  }, [docId]);

  const styleChunks = useMemo(() => {
    if (!chunkMap) {
      return [];
    }
    return Object.values(chunkMap)
      .filter((chunk) => chunk.type === DocumentChunkType.Style);
  }, [chunkMap]);

  const allChunkIdsToLoad = useMemo(() => {
    const styleChunkIds = styleChunks.map((chunk) => chunk.internal_id);
    return [...Object.keys(loadedDocumentChunkIds), ...styleChunkIds];
  }, [loadedDocumentChunkIds, styleChunks]);

  const chunkContentMap = useUnpackedDocumentChunks(chunkMap, allChunkIdsToLoad, isContentUnpackingDisabled, docId);

  const setChunkContentState = useCallback((chunkId: string, state: 'loaded' | 'unloaded') => {
    if (chunkId === 'single-chunk-full-content') {
      return;
    }
    setLoadedDocumentChunkIds((ids) => {
      switch (state) {
        case 'loaded':
          if (chunkId in ids) {
            return ids;
          }
          logger.debug(`chunk content from store LOAD`, { id: chunkId });
          return {
            ...ids,
            [chunkId]: true,
          };
        case 'unloaded': {
          if (!(chunkId in ids)) {
            return ids;
          }
          logger.debug(`chunk content from store UNLOAD`, { id: chunkId });
          const newIds = { ...ids };
          delete newIds[chunkId];
          return newIds;
        }
      }
    });
  }, []);

  useEffect(() => {
    if (chunkIndexesToLoad === undefined) {
      return;
    }
    const idsToLoad = Object.fromEntries(
      chunkIndexesToLoad
        .map((index) => spine?.[index])
        .filter(notEmpty)
        .map((id) => [id, true]),
    );
    setLoadedDocumentChunkIds((ids) => ({
      ...ids,
      ...idsToLoad,
    }));
  }, [chunkIndexesToLoad, spine]);

  const webviewDocumentChunks: WebviewDocumentChunk[] | undefined = useMemo(
    () => makeWebviewChunksFromDocumentChunks(chunkArray, chunkContentMap),
    [chunkContentMap, chunkArray],
  );

  const normalizedWebviewDocumentChunks: WebviewDocumentChunk[] = useMemo(() => {
    if (webviewDocumentChunks) {
      return webviewDocumentChunks;
    }
    if (unchunkedContent) {
      return [makeUnchunkedWebviewChunk(doc?.word_count, unchunkedContent)];
    }
    return [];
  }, [doc?.word_count, unchunkedContent, webviewDocumentChunks]);

  const webviewStyleChunks = useMemo(() => styleChunks
    .map((chunk) => {
      const content = chunkContentMap?.[chunk.internal_id]?.text ?? null;

      return {
        id: chunk.internal_id,
        filename: chunk.filename,
        content,
      };
    })
    .filter(notEmpty),
  [styleChunks, chunkContentMap]);

  const chunks: WebviewChunks = useMemo(() => ({
    document: normalizedWebviewDocumentChunks,
    style: webviewStyleChunks,
    toc,
    manifest,
    docId,
  }), [normalizedWebviewDocumentChunks, webviewStyleChunks, toc, manifest, docId]);

  useEffect(() => {
    const loaded = webviewDocumentChunks?.filter((c) => c.content);
    logger.debug(`chunks ${loaded?.length}/${webviewDocumentChunks?.length} loaded`, {
      loaded: loaded?.map((c) => [c.index, c.id]),
    });
  }, [webviewDocumentChunks]);

  return useMemo(
    () => ({
      setChunkContentState,
      chunks,
      isFetching,
    }),
    [chunks, isFetching, setChunkContentState],
  );
}
